import { memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsOverviewSection from "./InsightsOverviewSection";
import InsightsMuscleGroupsSection from "./InsightsMuscleGroupsSection";
import InsightsRepRangeSection from "./InsightsRepRangeSection";
import InsightsRpeDistributionSection from "./InsightsRpeDistributionSection";
import InsightsPowerliftingPerLiftSection from "./InsightsPowerliftingPerLiftSection";
import InsightsPowerliftingWeekSummary from "./InsightsPowerliftingWeekSummary";
import InsightsVolumeCoverageSection from "./InsightsVolumeCoverageSection";

/** Lenses that render per-lift cards + week summary instead of volume & coverage. */
function isPerLiftInsightsLens(lens) {
  return (
    lens === "powerlifting" ||
    lens === "olympic_weightlifting" ||
    lens === "strongman" ||
    lens === "streetlifting"
  );
}

/**
 * Per-lift bottom (PL / Olympic / Strongman / Streetlifting) vs volume & coverage (Hypertrophy, etc.).
 * @param {object} week — panel week payload
 * @param {string} lens
 * @param {function(string, object=): string} t — i18n `t` from `useTranslation`
 */
function perLiftBottomFromLens(week, lens, t) {
  if (lens === "streetlifting") {
    return {
      perLiftCards: week.streetliftingPerLiftCards,
      perLiftHeadingTitle: t(
        "programs.programInsights.panel.sectionStreetliftingPerLift"
      ),
      perLiftHeadingInfo: t(
        "programs.programInsights.panel.streetliftingPerLiftSectionInfo"
      ),
      perLiftWeekSummary: week.streetliftingWeekSummary,
    };
  }
  if (lens === "strongman") {
    return {
      perLiftCards: week.strongmanPerLiftCards,
      perLiftHeadingTitle: t(
        "programs.programInsights.panel.sectionStrongmanPerLift"
      ),
      perLiftHeadingInfo: t(
        "programs.programInsights.panel.strongmanPerLiftSectionInfo"
      ),
      perLiftWeekSummary: week.strongmanWeekSummary,
    };
  }
  if (lens === "olympic_weightlifting") {
    return {
      perLiftCards: week.olympicPerLiftCards,
      perLiftHeadingTitle: t(
        "programs.programInsights.panel.sectionOlympicPerLift"
      ),
      perLiftHeadingInfo: t(
        "programs.programInsights.panel.olympicPerLiftSectionInfo"
      ),
      perLiftWeekSummary: week.olympicWeekSummary,
    };
  }
  return {
    perLiftCards: week.perLiftCards,
    perLiftHeadingTitle: undefined,
    perLiftHeadingInfo: undefined,
    perLiftWeekSummary: week.powerliftingWeekSummary,
  };
}

/**
 * One week column: overview, muscles, rep ranges, RPE, then lens-specific bottom.
 *
 * Cross-column row sync: sections **2–5** use `data-insights-sync-row` (muscles → … → bottom lens block).
 * Overview is excluded so its block is not stretched with `min-height`. The week header is not tagged.
 *
 * @param {object} props
 * @param {object} props.week — panel week payload (see PropTypes).
 * @param {string} props.insightsVolumeSuffix — display suffix for overview volume (panel UNITS; not week-specific).
 * @param {"rpe"|"rir"} [props.insightsIntensityMeasure] — coach intensity preference; RPE vs RIR section labels.
 * @param {number} props.muscleBarGlobalMax — program-wide bar scale for Sets by muscle groups.
 */
function InsightsWeekColumn({
  week,
  weekHeaderLabel,
  lens,
  insightsVolumeSuffix,
  insightsIntensityMeasure = "rpe",
  muscleBarGlobalMax,
}) {
  const { t } = useTranslation("athlete-portal");
  const perLiftBottom = isPerLiftInsightsLens(lens)
    ? perLiftBottomFromLens(week, lens, t)
    : null;

  const weekHeaderText =
    weekHeaderLabel ?? t("programs.weekN", { n: week.weekIndex });

  return (
    <article
      className="ProgramInsights-weekColumn"
      aria-label={weekHeaderText}
    >
      <header className="ProgramInsights-weekHeader">
        <p className="s2 clipped w-fit max-w-full">
          {weekHeaderText}
        </p>
      </header>
      <div className="ProgramInsights-weekBody">
        <InsightsOverviewSection
          week={week}
          insightsVolumeSuffix={insightsVolumeSuffix}
          insightsIntensityMeasure={insightsIntensityMeasure}
        />
        <InsightsMuscleGroupsSection
          muscles={week.muscles}
          muscleBarGlobalMax={muscleBarGlobalMax}
          syncRowIndex={2}
        />
        <InsightsRepRangeSection
          buckets={week.repRanges}
          syncRowIndex={3}
        />
        <InsightsRpeDistributionSection
          buckets={week.rpeDistribution}
          syncRowIndex={4}
          insightsIntensityMeasure={insightsIntensityMeasure}
        />
        {perLiftBottom ? (
          <>
            <InsightsPowerliftingPerLiftSection
              lifts={perLiftBottom.perLiftCards}
              syncRowIndex={5}
              headingTitle={perLiftBottom.perLiftHeadingTitle}
              headingInfo={perLiftBottom.perLiftHeadingInfo}
              insightsIntensityMeasure={insightsIntensityMeasure}
            />
            <InsightsPowerliftingWeekSummary
              summary={perLiftBottom.perLiftWeekSummary}
              lensId={lens}
            />
          </>
        ) : (
          <InsightsVolumeCoverageSection
            volumeCoverage={week.volumeCoverage}
            syncRowIndex={5}
          />
        )}
      </div>
    </article>
  );
}

InsightsWeekColumn.propTypes = {
  /** Discipline lens: PL / Olympic / Strongman / Streetlifting use per-lift cards + week footer; others use volume & coverage. */
  lens: PropTypes.string.isRequired,
  insightsIntensityMeasure: PropTypes.oneOf(["rpe", "rir"]),
  insightsVolumeSuffix: PropTypes.string.isRequired,
  muscleBarGlobalMax: PropTypes.number.isRequired,
  weekHeaderLabel: PropTypes.string,
  week: PropTypes.shape({
    weekIndex: PropTypes.number.isRequired,
    overview: PropTypes.array.isRequired,
    muscles: PropTypes.array.isRequired,
    repRanges: PropTypes.array.isRequired,
    rpeDistribution: PropTypes.array.isRequired,
    perLiftCards: PropTypes.array.isRequired,
    powerliftingWeekSummary: PropTypes.object.isRequired,
    olympicPerLiftCards: PropTypes.array.isRequired,
    olympicWeekSummary: PropTypes.object.isRequired,
    strongmanPerLiftCards: PropTypes.array.isRequired,
    strongmanWeekSummary: PropTypes.object.isRequired,
    streetliftingPerLiftCards: PropTypes.array.isRequired,
    streetliftingWeekSummary: PropTypes.object.isRequired,
    volumeCoverage: PropTypes.object.isRequired,
  }).isRequired,
};

export default memo(InsightsWeekColumn);
